import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { trackViewItemList, trackSelectItem, mapProductToGA4Item } from 'helpers/analytics/ga4Events';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { resolveTextConfiguration } from 'helpers/hooks/useResolvedLocalizedObject';
import { desktop } from 'helpers/utils/screensizes';
import { useFeatures } from 'providers/feature-flag';
import { Product } from 'types/entity/product';
import { useProduct } from 'frontastic';
import ProductGridWrapper from 'components/HOC/product-banner-wrapper/components/banner-products-grid';
import { getHeadingStyles } from '../hero-banner/constant';
import PlpProductTileWrapper from '../product/product-list/components/plp-product-tile-wrapper';
import { useCookies } from 'next-client-cookies';

export interface Props {
  data: any;
}

const PromotionalProductBanner: React.FC<Props> = ({ data }) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { query } = useProduct();
  const texts = data?.text?.dataSource?.config;
  const [products, setProducts] = useState<Product[]>([]);
  const { headline, productsList, promotionalBgColorLight } = data;
  const cookies = useCookies();
  const { locale: paramLocale } = useParams();
  const locale = (cookies.get('locale') as string | undefined) ?? paramLocale;
  const resolved = resolveTextConfiguration(texts, locale);
  const tileCTA = data?.tileCTA;
  useEffect(() => {
    const fetchProducts = async () => {
      const valuesOnly = productsList.map(({ productKey }: any) => productKey);

      query({
        productKeys: valuesOnly,
      }).then((res: any) => {
        setProducts(res?.data?.items as Product[]);
      });
    };

    fetchProducts();
  }, [query, productsList]);

  // GA4: view_item_list
  const viewItemListFired = useRef(false);
  const {enableGA4} = useFeatures();
  useEffect(() => {
    if (products?.length > 0 && !viewItemListFired.current) {
      const listName = 'Promotional Products';
      const items = products?.map((product, index) => mapProductToGA4Item(enableGA4(),product, { index, itemListName: listName }));
      trackViewItemList(listName, items);
      viewItemListFired.current = true;
    }
  }, [products]);
  return (
    <div className="mx-12 sm:mx-24 md:mx-75 lg:mx-76 xl:mx-0">
      <div className="container text-center">
        {products?.length > 0 && (
          <div
            className="w-full items-center justify-center gap-0 py-20 lg:pb-68 lg:pt-40"
            style={{ background: promotionalBgColorLight }}
          >
            <div data-testid="section-heading-promotional-products" className={`px-16 pb-20`}>
              <div className={`section-heading`} data-ffid="ctr-section-heading">
                <div>
                  <h2
                    className={`whitespace-normal break-words pt-24`}
                    style={getHeadingStyles(headline, isDesktopSize)}
                    data-ffid={`lbl-promotional-products-main-heading`}
                  >
                    {headline[0].headlineLabel}
                  </h2>
                </div>
              </div>
          </div>
            <ProductGridWrapper ffid="promotion">
              {products?.map((product, index) => (
                <PlpProductTileWrapper
                  ffid="promotion"
                  key={product.productId}
                  product={product}
                  index={index}
                  onClick={() => {
                    trackSelectItem('Promotional Products', [
                      mapProductToGA4Item(enableGA4(),product, { index, itemListName: 'Promotional Products' }),
                    ]);
                  }}
                  tileCTA={tileCTA}
                  textConfig={resolved?.textConfiguration?.[0]}
                  tieredPrice={resolved?.tieredPrice}
                  calendarOptions={resolved?.calendarOptions}
                />
              ))}
            </ProductGridWrapper>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromotionalProductBanner;
